import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const instagramUnfollowProProduct: Product = {
  id: "instagram-unfollow-pro",
  slug: "instagram-unfollow-pro",
  name: "Unfollow Pro for Instagram — Bulk Unfollow With Filters",
  alternateName: "Unfollow Pro for Instagram",
  shortName: "Unfollow Pro for Instagram",
  url: "https://plugmonkey.xyz/product/instagram-unfollow-pro/",
  chromeStoreRedirect: "/redirect/instagram-unfollow-pro-chrome-store/",
  description:
    "Bulk unfollow on Instagram with smart filters: search by name, target inactive accounts, skip verified/private. Free Chrome extension.",
  shortDescription:
    "Smart Instagram Following Management - Search, Filter, and Unfollow with Precision",
  image: ["https://plugmonkey.xyz/product/instagram-unfollow-pro/og-image.png"],
  icon: {
    name: "HiUserRemove",
    backgroundClasses: "bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] text-white",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Social Media Management",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Social Media Users",
  freeOffer: {
    name: "Free Version",
    description: "Unfollow up to 10 accounts per week with advanced filtering features",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/instagram-unfollow-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/84dd09f3-bf2a-4595-8660-52b0ba05f935",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/a308e793-e079-4c5d-bef3-cda3b755ed93",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/86fd2360-4ef4-461d-abfc-00c427f98cc1",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.8",
    reviewCount: "24",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "12,470",
  keywords:
    "Instagram unfollow tool, Instagram unfollow extension, search Instagram following list, filter Instagram accounts, mass unfollow Instagram, bulk unfollow Instagram, unfollow inactive Instagram users, Instagram follower management, Instagram cleanup tool, Instagram feed cleaner, Instagram account manager, unfollow by keyword, Instagram feed curator, social media management extension",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.4.11",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.4.11",
  hero: {
    headline:
      "<span class='text-[#E1306C]'>Unfollow Pro for Instagram</span> — Bulk Unfollow Instagram Accounts With Surgical Precision",
    subheadline:
      "Take back control of your Instagram feed. Advanced search, powerful filters, and bulk unfollow capabilities to curate your following list exactly how you want it.",
    ctaText: "Install Free",
    badgeText: "v1.4.11 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "Advanced Search & Filtering",
      description:
        "Search by username or name keywords. Filter by verified status, private accounts, and profile pictures to find exactly who you want to unfollow.",
      icon: "HiSearch",
    },
    {
      title: "Safe & Secure",
      description:
        "Chrome extension that works directly in your browser. No passwords, no API keys, no third-party access to your Instagram account.",
      icon: "HiShieldCheck",
    },
    {
      title: "Bulk Operations",
      description:
        "Mass unfollow every filtered account with one click, then leave it running. It paces itself between unfollows and pauses on its own if Instagram pushes back.",
      icon: "HiLightningBolt",
    },
  ],
  longFormFeatures: [
    {
      title: "Name & Username Search",
      description:
        "Type any keyword to find accounts with matching text in their name or username. Perfect for finding specific types of accounts (fitness, coach, photography) or identifying accounts you want to unfollow.",
      bullets: [
        "Search by username keywords",
        "Search by name keywords",
        "Case-insensitive matching",
        "Real-time filtering",
      ],
    },
    {
      title: "Verified Account Filtering",
      description:
        "Show or hide verified accounts with a single toggle. Useful for separating brand accounts from regular users, or finding verified accounts you're following.",
      bullets: [
        "Toggle verified accounts on/off",
        "Identify brand vs. personal accounts",
        "Quick filtering for account cleanup",
      ],
    },
    {
      title: "Private Account Filtering",
      description:
        "Filter accounts based on their privacy status. Show only public accounts, only private accounts, or see all accounts together.",
      bullets: [
        "Filter by public/private status",
        "Find accounts that went private",
        "Identify public creators easily",
      ],
    },
    {
      title: "Profile Picture Detection",
      description:
        "Find accounts with no profile picture—often inactive or spam accounts—and clear them out in one paced run.",
      bullets: [
        "Detect missing profile pictures",
        "Identify potential spam accounts",
        "Unfollow them in one paced run",
      ],
    },
    {
      title: "Protected Accounts",
      description:
        "Create a 'never unfollow' list for your favorites. Unfollow with confidence knowing you'll never lose important connections, even during bulk operations.",
      bullets: [
        "Manually protect important accounts",
        "Bulk unfollow safely",
        "Never lose key connections",
        "Easy add/remove from protected list",
      ],
    },
    {
      title: "Bulk Unfollow (PRO)",
      description:
        "Set your filters, review the results, then click 'Mass Unfollow' to work through every matching account unattended, instead of tapping Following → Unfollow hundreds of times yourself.",
      bullets: [
        "Unfollow hundreds of accounts in one unattended run",
        "Automatic breaks every 25, 50 (default), 100 or 200 unfollows",
        "Works with all filters",
        "Safe with protected accounts",
        "Smart rate limiting",
      ],
    },
  ],
  faq: [
    {
      question: "How does Unfollow Pro for Instagram work?",
      answer:
        "Unfollow Pro for Instagram is a Chrome extension that adds an orange button directly on Instagram.com. Click it to open a panel where you can search, filter, and unfollow accounts by username, verified status, or private status. The free version allows you to unfollow up to 10 accounts per week manually, while the Pro version offers unlimited manual unfollows plus bulk unfollow capabilities.",
    },
    {
      question: "Is Unfollow Pro for Instagram safe to use?",
      answer:
        "It is built to keep the risk low. Every unfollow is sent from your own logged-in browser session, with a randomized gap before the next one: Conservative (15-25 seconds, the default), Normal (5-10) or Fast (3-5), plus a short automatic break after every 50 unfollows by default (25, 50, 100 or 200, or off), lasting about a quarter of the time that batch took. When Instagram refuses an action, the run pauses (10 minutes, then 20), slows down, and stops on the third refusal in a row instead of pushing on. Instagram publishes no official limit and no tool can guarantee an account won't be restricted, so keep large clean-ups to several shorter sessions. We don't store your Instagram credentials or personal data, and we never sell or share your information.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version allows you to unfollow up to 10 accounts per week manually with full access to advanced search and filtering features. The Pro version offers unlimited manual unfollows, bulk unfollow (mass unfollow all filtered accounts), and removes the weekly limit completely.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "Can I use Unfollow Pro for Instagram on multiple devices?",
      answer:
        "Yes! Your Pro license is tied to your email/account, so you can use Unfollow Pro for Instagram on any device where you have the extension installed.",
    },
    {
      question: "Will people know if I unfollow them using Unfollow Pro for Instagram?",
      answer:
        "No, Unfollow Pro for Instagram unfollows accounts in the same way as if you were doing it manually. The accounts you unfollow won't receive any special notification that you've used our tool.",
    },
    {
      question: "How does Unfollow Pro for Instagram compare to Inflact?",
      answer:
        'Unfollow Pro for Instagram is a lightweight Chrome extension focused specifically on searching, filtering, and unfollowing Instagram accounts safely. Inflact is a broader automation suite that handles scheduling, DMs, and hashtag research but is heavier and carries more risk with Instagram\'s terms of service. For a focused, safe unfollowing tool, Unfollow Pro for Instagram is the better fit. See the <a href="/comparison/instagram-unfollow-pro-vs-inflact/" style="color: var(--primary); text-decoration: underline;">full comparison with Inflact</a> or browse the <a href="/best/best-instagram-unfollow-tools/" style="color: var(--primary); text-decoration: underline;">best Instagram unfollow tools</a>.',
    },
  ],
  relatedResources: [
    {
      category: "best",
      slug: "best-instagram-unfollow-tools",
      title: "Best Instagram Unfollow Tools 2026 — Ranked",
      description: "Independent ranking of IG unfollow tools. Unfollow Pro for Instagram #1.",
    },
    {
      category: "guide",
      slug: "how-to-mass-unfollow-on-instagram",
      title: "How to Mass Unfollow on Instagram Safely",
      description: "Workflow that respects IG's rate limits — no account flags.",
    },
    {
      category: "faq",
      slug: "how-many-accounts-can-i-unfollow-on-instagram-per-day",
      title: "How Many Accounts Can I Unfollow Per Day?",
      description: "The IG rate-limit ceiling + how to stay under it for years.",
    },
    {
      category: "faq",
      slug: "does-instagram-show-who-viewed-your-profile",
      title: "Does Instagram Show Who Viewed Your Profile?",
      description: "Direct answer: no — and why all 'profile view tracker' apps are scams.",
    },
    {
      category: "comparison",
      slug: "instagram-unfollow-pro-vs-inflact",
      title: "Unfollow Pro for Instagram vs Inflact",
      description: "Focused tool vs full automation suite — safety + cost compared.",
    },
    {
      category: "alternative",
      slug: "inflact",
      title: "Inflact Alternative — Lightweight Safety-First Replacement",
      description:
        "Inflact's automation suite is overkill for unfollowing — here's the focused tool.",
    },
  ],
  testimonials: [
    {
      text: "Went from following 2,800 to 400 on our brand account over a weekend. The whitelist saved us — our brand partners and collaborators stayed protected while we cleaned out the junk.",
      author: "Fatima H.",
      role: "Manages social for a DTC skincare brand",
      rating: 5,
    },
    {
      text: "I own a bakery and my business IG somehow followed 3,000+ random accounts over the years. This let me search by bio keyword and mass-unfollow anyone not food-related. Only complaint: the built-in delays feel slow, but I get why they're there.",
      author: "Owen B.",
      role: "Small business owner, @owensbakes",
      rating: 4,
    },
    {
      text: "My engagement rate jumped from 2.1% to 3.8% after cleaning up my following list. Turns out Instagram's algorithm cares about your follow ratio more than people think. The inactive filter found 600+ ghost accounts I didn't even know I followed.",
      author: "Yuki T.",
      role: "Travel photographer, 45K followers",
      rating: 5,
    },
    {
      text: "My personal brand account had 2,000 follows from a phase where I followed everyone back. Filtered by bio keyword and kept only fitness and nutrition accounts. My feed is finally useful for research again.",
      author: "Tariq B.",
      role: "Online fitness coach",
      rating: 5,
    },
    {
      text: "Cleaned a few hundred inactive follows off my studio account and protected my supplier and gallery contacts with the whitelist first. The pacing is slow, so I just let it run overnight.",
      author: "Grace N.",
      role: "Ceramic artist, @gracemadeit",
      rating: 4,
    },
  ],
  useCases: [
    {
      title: "Social Media Managers",
      description:
        "Maintain clean, professional following lists for client accounts. Quickly identify and remove inactive or low-quality accounts using advanced filters.",
    },
    {
      title: "Content Creators & Influencers",
      description:
        "Curate your following list to stay focused on accounts that align with your niche. Use keyword search to find and unfollow accounts that no longer match your content strategy.",
    },
    {
      title: "Marketing Professionals",
      description:
        "Clean up brand accounts by filtering for spam indicators (no profile picture). Maintain healthy follower-to-following ratios for better brand perception.",
    },
    {
      title: "Regular Instagram Users",
      description:
        "Take back control of your feed. Remove accounts you followed years ago that no longer interest you. Search by keywords to find specific account types.",
    },
  ],
  ctaSection: {
    title: "Ready to Clean Up Your Instagram Feed?",
    subtitle:
      "Join users who have transformed their Instagram experience with Unfollow Pro for Instagram",
  },
  productHunt: {
    slug: "instagram-unfollow-pro-by-plugmonkey",
    id: "instagram-unfollow-pro",
    title: "Unfollow Pro for Instagram",
    rating: "4.8",
    reviewCount: "24",
  },
  demoSection: {
    title: "See Unfollow Pro for Instagram in Action",
    description: "Watch a filtered, paced Instagram clean-up from start to finish.",
    slides: getProductScreenshots("instagram-unfollow-pro"),
  },
  howTo: {
    name: "How to Use Unfollow Pro for Instagram",
    description:
      "Follow these simple steps to search, filter, and unfollow Instagram accounts with surgical precision using Unfollow Pro for Instagram.",
    totalTime: "PT5M",
    tool: "Unfollow Pro for Instagram Chrome Extension",
    steps: [
      {
        name: "Install the Extension",
        text: "Click the 'Install Free' button to add Unfollow Pro for Instagram to your Chrome browser from the Chrome Web Store. The extension will be installed automatically.",
        url: "https://plugmonkey.xyz/product/instagram-unfollow-pro/",
        position: 1,
      },
      {
        name: "Navigate to Instagram",
        text: "Go to instagram.com and log in. The extension activates on any Instagram page.",
        url: "https://www.instagram.com/",
        position: 2,
      },
      {
        name: "Open Unfollow Pro for Instagram",
        text: "Click the orange Unfollow Pro for Instagram button that appears on the page. This opens the extension modal with your account info and unfollow controls.",
        position: 3,
      },
      {
        name: "Apply Filters and Search",
        text: "Use the advanced search to find accounts by username or name keywords. Apply filters for verified status, private/public accounts, or profile pictures to target specific accounts.",
        position: 4,
      },
      {
        name: "Protect Important Accounts (Optional)",
        text: "Add accounts to your protected list to ensure you never accidentally unfollow important connections, even during bulk operations.",
        position: 5,
      },
      {
        name: "Unfollow Accounts",
        text: "Review filtered results and unfollow accounts individually (Free: up to 10 per week), or use Mass Unfollow to work through all filtered accounts in one paced run (PRO feature).",
        position: 6,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "FaInstagram",
    backgroundIconOpacity: "opacity-5",
    backgroundIconTransform: "transform rotate-12",
    features: [
      { label: "Filters", value: "Verified / Private" },
      { label: "Search", value: "Name & Username" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 5,
  headerOrder: 300,
  pricing: {
    tagline: "Smart Instagram Following Management - Search, Filter, and Unfollow with Precision",
    freeFeatures: [
      "10 unfollows per week",
      "Advanced name & username search",
      "Verified account filtering",
      "Private/Public account filtering",
      "Profile picture detection",
      "Protected accounts list",
    ],
    proFeatures: [
      "Unlimited manual unfollows (no weekly limit)",
      "Bulk unfollow (mass unfollow filtered accounts)",
      "All search & filter features",
      "Protected accounts",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Perfect for curating your Instagram feed and removing clutter",
  },
  flowData: {
    installation: {
      headline: "You're ready to take control of your Instagram feed.",
      subheadline: "Here is how to get the most out of Unfollow Pro for Instagram in 30 seconds.",
      guideSteps: [
        {
          title: "Pin the Extension",
          description:
            "Click the puzzle piece icon in your Chrome toolbar and pin Unfollow Pro for Instagram for easy access.",
          image: "pin-extension",
        },
        {
          title: "Open on Instagram",
          description:
            "Go to instagram.com and click the orange 'Open Unfollow Pro for Instagram' button that appears on the page.",
          image: "navigate-instagram",
          screenshot: "screenshot-1.png",
          ctaText: "Open Instagram",
          ctaUrl: "https://www.instagram.com",
        },
        {
          title: "Filter & Unfollow",
          description:
            "Search by username, filter by verified or private status, then unfollow individually or in bulk.",
          image: "start-unfollow",
          screenshot: "screenshot-1.png",
        },
      ],
      primaryCtaText: "Open Instagram and Start Managing",
      primaryCtaUrl: "https://www.instagram.com",
      emailCapture: {
        headline: "Get weekly Instagram growth tactics",
        description:
          "Join power users receiving weekly tips on Instagram growth and audience management. No fluff, just tactics.",
        buttonText: "Send me the tactics",
      },
    },
    uninstallation: {
      headline: "Sorry to see you go.",
      subheadline: "Feedback is a gift. Tell us how we can improve.",
      survey: {
        question: "What was the main reason for uninstalling?",
        options: [
          "It didn't work as expected",
          "I found a better alternative",
          "It's too expensive",
          "I don't need it anymore",
          "Other",
        ],
      },
    },
  },
};
